import type { DomainItem, DeletedDomainItem } from "@/types";

type DomainsListResponse = {
  success: boolean;
  message: string;
  data: DomainItem[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
};

import { atom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";

import { apiClient } from "@/utils/api";
import { buildApiParams } from "@/utils/helpers";
import { createPaginationParams, createTableQueryOptions } from "@/components/table";

// API Endpoints
const DOMAINS_BASE = "/domains";
const ORGANIZATION_BASE = "/organization";

export type DomainListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | undefined>;
  refresh?: number;
};

export type DeletedDomainsListParams = {
  page: number;
  per_page: number;
  search?: string;
  filters?: Record<string, string | undefined>;
  refresh?: number;
};

export const domainsListParamsAtom = atom<DomainListParams>({
  ...createPaginationParams(),
  filters: {},
});

export const deletedDomainsListParamsAtom = atom<DeletedDomainsListParams>({
  ...createPaginationParams(),
  filters: {},
});

// Hydration flag to delay initial query until URL/filters are synced
export const domainsHydratedAtom = atom<boolean>(false);

export const domainsQueryAtom = atomWithQuery<DomainsListResponse>((get) => {
  const params = get(domainsListParamsAtom);
  const hydrated = get(domainsHydratedAtom);

  return createTableQueryOptions({
    queryKey: ["domains", params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        { page: params.page, per_page: params.per_page },
        params.filters || {},
      );

      const { data } = await apiClient.get(DOMAINS_BASE, { params: apiParams });

      // Return full typed response
      return data as DomainsListResponse;
    },
    enabled: hydrated
  });
});

export const deletedDomainsQueryAtom = atomWithQuery<{
  success: boolean;
  message: string;
  data: DeletedDomainItem[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}>((get) => {
  const params = get(deletedDomainsListParamsAtom);

  return createTableQueryOptions({
    queryKey: ["deleted-domains", params] as const,
    queryFn: async () => {
      // Build params manually to avoid filter[] wrapper
      const apiParams: Record<string, any> = {};

      // Add pagination params
      if (params.page && params.page !== 1) {
        apiParams.page = params.page;
      }
      if (params.per_page && params.per_page !== 15) {
        apiParams.per_page = params.per_page;
      }

      // Add search filter
      if (params.search) {
        apiParams['filter[search]'] = params.search;
      }

      // Add filters
      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value) {
            apiParams[`filter[${key}]`] = value;
          }
        });
      }

      // Add only_trashed directly (not as filter)
      apiParams.only_trashed = 'true';

      const { data } = await apiClient.get(DOMAINS_BASE, { params: apiParams });

      // Return full typed response
      return data as {
        success: boolean;
        message: string;
        data: DeletedDomainItem[];
        meta: {
          total: number;
          per_page: number;
          current_page: number;
          last_page: number;
        };
      };
    }
  });
});


// Actions (API services)
export async function deleteDomain(domainId: number | string): Promise<any> {
  const { data } = await apiClient.delete(`${DOMAINS_BASE}/${domainId}`);

  return data;
}




export async function updateDomainDebug(domainId: number | string, is_debug: boolean): Promise<any> {
  const { data } = await apiClient.put(`${DOMAINS_BASE}/${domainId}/debug`, { is_debug });

  return data;
}


export async function updateDomainTracking(domainId: number | string, is_tracking: boolean): Promise<any> {
  const { data } = await apiClient.put(`${DOMAINS_BASE}/${domainId}/tracking`, { is_tracking });

  return data;
}

type DomainExtraPayload = {
  force_click_tracking: boolean;
  capture_all_properties: boolean;
  referrer_tracking: boolean;
  ignore_bots: boolean;
  enable_live_interaction: boolean;
};

export async function setDomainExtra(
  domainId: number | string,
  extra: DomainExtraPayload,
): Promise<any> {
  const { data } = await apiClient.put(`${DOMAINS_BASE}/${domainId}/set-extra`, { extra });

  return data;
}


export async function verifyDomain(domainId: number | string): Promise<any> {
  const { data } = await apiClient.put(`${DOMAINS_BASE}/${domainId}/verify`);

  return data;
}


export async function unverifyDomain(domainId: number | string): Promise<any> {
  const { data } = await apiClient.put(`${DOMAINS_BASE}/${domainId}/un-verify`);

  return data;
}

export type AddDomainPayload = {
  title: string;
  domain: string;
  token_limit?: number | null;
};

export async function addDomainToOrganization(organizationId: number, domainData: AddDomainPayload): Promise<any> {
  const payload: Record<string, string | number | null | undefined> = {
    title: domainData.title,
    domain: domainData.domain
  };
  if (domainData.hasOwnProperty('token_limit')) {
    payload.token_limit =
      typeof domainData.token_limit === 'number' ? domainData.token_limit : null;
  }
  const { data } = await apiClient.post(`${ORGANIZATION_BASE}/${organizationId}/domains`, payload);
  return data;
}

export type UpdateDomainPayload = {
  title?: string;
  token_limit?: number | null;
};

export async function updateDomain(domainId: number | string, payload: UpdateDomainPayload): Promise<any> {
  const body: Record<string, string | number | null | undefined> = {};
  if (payload.title !== undefined) body.title = payload.title;
  if (payload.hasOwnProperty('token_limit')) body.token_limit = payload.token_limit ?? null;
  const { data } = await apiClient.put(`${DOMAINS_BASE}/${domainId}`, body);
  return data;
}

export async function restoreDomain(domainId: number | string): Promise<any> {
  const { data } = await apiClient.post(`${DOMAINS_BASE}/${domainId}/restore`);

  return data;
}

// Set Domain CDN
export const setDomainCdn = atom(
  null,
  async (
    get,
    set,
    {
      domainId,
      cdnId,
      cdnUrlOverride
    }: { domainId: number; cdnId?: number; cdnUrlOverride?: string }
  ) => {
    const payload: { cdn_id?: number; cdn_url_override?: string } = {};

    if (cdnUrlOverride) {
      payload.cdn_url_override = cdnUrlOverride;
    }
    if (cdnId) {
      payload.cdn_id = cdnId;
    }

    await apiClient.post(`${DOMAINS_BASE}/${domainId}/set-cdn`, payload);

    // Trigger refresh by updating params
    const currentParams = get(domainsListParamsAtom);
    set(domainsListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);


